import { useEffect, useRef, useState } from "react"

import useDebounce from "@hooks/useDebounce"

import { CLEAR_SEARCH_EVENT } from "@constants/events"

import { SearchInputProps } from "./SearchInput.types"
import SearchInputChrome from "./SearchInputChrome/SearchInputChrome"

const MIN_SEARCH_LENGTH = 2

export default function SearchInput(props: SearchInputProps) {
    const {
        type = "bar",
        defaultValue,
        onValueChange,
        placeholder = "Search...",
        isLoading = false,
        isDisabled = false,
        _isFocused,
        _isHovered,

        ...rest
    } = props

    const [value, setValue] = useState<string>(defaultValue)
    const debouncedValue = useDebounce<string>(value, 500)

    const inputRef = useRef<HTMLInputElement>(null)

    const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setValue(event.target.value)
    }

    const handleClear = () => {
        setValue("")
        if (!isDisabled) {
            inputRef.current?.focus()
        }
    }

    useEffect(() => {
        setValue(defaultValue)
    }, [defaultValue])

    useEffect(() => {
        if (debouncedValue.length >= MIN_SEARCH_LENGTH) {
            onValueChange(debouncedValue)
        } else {
            onValueChange("")
        }
    }, [debouncedValue])

    useEffect(() => {
        window.addEventListener(CLEAR_SEARCH_EVENT, handleClear)

        return () => {
            window.removeEventListener(CLEAR_SEARCH_EVENT, handleClear)
        }
    }, [])

    return (
        <SearchInputChrome
            type={type}
            renderInput={(className) => (
                <input
                    inputMode="search"
                    enterKeyHint="search"
                    {...rest}
                    value={value}
                    onChange={onInputChange}
                    placeholder={placeholder}
                    className={className}
                    ref={inputRef}
                    disabled={isDisabled}
                    aria-disabled={isDisabled}
                />
            )}
            showClearButton={value !== ""}
            onClear={handleClear}
            isLoading={isLoading}
            isDisabled={isDisabled}
            _isFocused={_isFocused}
            _isHovered={_isHovered}
        />
    )
}
